import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO, { organizationSchema } from '../components/SEO';
import { track } from '../lib/analytics';
import api from '../lib/api';
import LandingHeader from '../components/landing/LandingHeader';
import Hero from '../components/landing/Hero';
import ProofBoard from '../components/landing/ProofBoard';
import PainChecklist from '../components/landing/PainChecklist';
import OfferStack from '../components/landing/OfferStack';
import Compare from '../components/landing/Compare';
import HowItWorks from '../components/landing/HowItWorks';
import Results from '../components/landing/Results';
import { Faq, FinalCta, StickyCta, Footer } from '../components/landing/Closing';
import { orderedSections } from '../components/landing/sections';
import { useTrial } from '../contexts/TrialContext';

function useCountdown(endsAt) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endsAt) return undefined;
    const tick = () => {
      const ms = new Date(endsAt) - new Date();
      if (ms <= 0) { setLeft(null); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return left;
}

function CountdownBanner({ timer, onCta }) {
  const left = useCountdown(timer?.ends_at);
  if (!timer?.enabled || !left) return null;
  return (
    <div className="bg-slate-900 text-white" data-testid="countdown-banner">
      <button onClick={onCta} className="w-full flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2.5 text-[12.5px] sm:text-[13.5px] text-center hover:bg-slate-800 transition" data-testid="countdown-banner-cta">
        <span className="font-medium text-white/90">{timer.text}</span>
        <span className="font-mono font-bold text-amber-400 tabular-nums tracking-wider" data-testid="countdown-timer-value">{left}</span>
        {timer.cta_label && (
          <span className="hidden sm:inline-flex items-center gap-1 font-semibold text-amber-300 underline underline-offset-4 decoration-amber-300/40">
            {timer.cta_label} <ArrowRight size={12} />
          </span>
        )}
      </button>
    </div>
  );
}

function useHeroPassed(ref, ready) {
  const [passed, setPassed] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!ready || !el) return undefined;
    const obs = new IntersectionObserver(([e]) => setPassed(!e.isIntersecting && e.boundingClientRect.top < 0), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, ready]);
  return passed;
}

/* Publishes the fixed banner+header height as --lp-top so the hero and scroll offsets follow it. */
function useTopOffset(ref, ready) {
  useEffect(() => {
    const el = ref.current;
    if (!ready || !el) return undefined;
    const apply = () => document.documentElement.style.setProperty('--lp-top', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty('--lp-top'); };
  }, [ref, ready]);
}

export default function Landing() {
  const navigate = useNavigate();
  const { trialMode, price: trialPrice, hours: trialHours } = useTrial();
  const [cfg, setCfg] = useState(null);
  const heroRef = useRef(null);
  const topRef = useRef(null);
  const heroPassed = useHeroPassed(heroRef, !!cfg);
  useTopOffset(topRef, !!cfg);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('ref_code', refCode);
      localStorage.setItem('ref_code_at', Date.now().toString());
    }
    api.get('/api/landing/config').then(({ data }) => setCfg(data)).catch(() => setCfg({}));
  }, []);

  const c = cfg || {};
  const hero0 = c.hero || {};
  // Trial mode (Admin → Free vs Trial): the "start free" secondary CTAs become the trial offer.
  const trialSecondary = `Start the ${trialPrice} trial — ${trialHours} hours of access`;
  const hero = trialMode && hero0.secondary_text ? { ...hero0, secondary_text: trialSecondary } : hero0;
  const finalCta = trialMode && c.final_cta?.cta_free ? { ...c.final_cta, cta_free: `or start the ${trialPrice} trial` } : (c.final_cta || {});
  const ctaIntent = hero.cta_target === 'free' ? 'free' : 'pro';

  const goCta = (location) => {
    track('cta_click', { location, label: 'get_instant_access', intent: ctaIntent });
    track('signup_intent', { source: location });
    navigate(`/signup?intent=${ctaIntent}`);
  };
  const goFree = (location) => {
    track('cta_click', { location, label: 'start_free' });
    navigate('/signup?intent=free');
  };

  const testimonials = useMemo(() => c.testimonials || [], [c.testimonials]);
  const videos = testimonials.filter(t => t.type === 'video' && t.video_url);
  const images = testimonials.filter(t => t.type === 'image' && t.image_url);
  const quotes = testimonials.filter(t => t.type === 'text' && t.quote);

  if (!cfg) {
    return (
      <div className="min-h-screen bg-[#f5f1eb] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const proof = c.signup_stats_resolved && c.signup_stats_resolved.count > 0 ? c.signup_stats_resolved : null;
  const videoUrl = hero.video_url || (c.watch_first || {}).video_url || '';
  const sections = orderedSections(c.layout);
  const hiddenIds = new Set(sections.filter(s => !s.visible).map(s => s.id));
  const nav = { ...(c.nav || {}), links: (c.nav?.links || []).filter(l => !hiddenIds.has(l.target)) };

  const render = {
    hero: () => <Hero hero={hero} proof={proof} videoUrl={videoUrl} onCta={goCta} onSecondary={goFree} heroRef={heroRef} />,
    teacher: () => <ProofBoard teacher={c.teacher || {}} onCta={goCta} protection={c.protection} />,
    pains: () => <PainChecklist pains={c.pains || {}} onCta={goCta} />,
    whats_inside: () => <OfferStack inside={c.whats_inside || {}} onCta={goCta} onSecondary={hero.secondary_text ? goFree : null} secondaryLabel={trialMode ? trialSecondary : undefined} />,
    compare: () => <Compare compare={c.compare || {}} />,
    how: () => <HowItWorks how={c.how || {}} onCta={goCta} />,
    results: () => <Results results={c.results || {}} videos={videos} images={images} quotes={quotes} onCta={goCta} protection={c.protection} />,
    faq: () => <Faq faq={c.faq || {}} />,
    final_cta: () => <FinalCta finalCta={finalCta} onCta={goCta} onSecondary={goFree} />,
  };

  return (
    <div className="min-h-screen bg-[#f5f1eb]" data-testid="landing-page" data-section-order={sections.filter(s => s.visible).map(s => s.key).join(',')}>
      <SEO
        title={null}
        description="You don't need 6 months of coaching to score CLB 9+ in CELPIP. Mock tests built like the real exam, scored the way the examiner scores."
        path="/"
        jsonLd={organizationSchema}
      />
      <div ref={topRef} className="fixed top-0 inset-x-0 z-50" data-testid="landing-top-bar">
        <CountdownBanner timer={c.timer} onCta={() => goCta('countdown_banner')} />
        <LandingHeader nav={nav} onCta={goCta} />
      </div>

      {sections.filter(s => s.visible).map(s => <React.Fragment key={s.key}>{render[s.key]()}</React.Fragment>)}
      <Footer />
      <StickyCta cfg={c.sticky_cta} price={(c.whats_inside || {}).price} show={heroPassed} onCta={goCta} />
    </div>
  );
}
